import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  Alert, Button, Card, Modal, Space, Spin, Steps, Table, Tag, theme, Tooltip, Typography,
  message,
} from 'antd';
import dayjs from 'dayjs';
import useAuth from '../../hooks/usePropelAuth';
import {
  extractIndices, startRecompute, getIndicesDraft, getIndicesRevisions, IndicesApiError,
  publishIndices, restoreIndicesRevision, retryIndicesDeploy, saveIndicesDraft,
  validateIndicesDraft,
} from '../../api/carbon_indices';
import StepGenerate from './steps/StepGenerate';
import StepIntegrity from './steps/StepIntegrity';
import StepPricing from './steps/StepPricing';
import StepReview from './steps/StepReview';
import PublishStatus from './PublishStatus';
import { indicesLiveUrl, indicesPreviewLabel, indicesPreviewUrl } from './previewUrl';
import { EDITOR_ROLES, PUBLISHER_ROLES } from './roles';
import { S } from './ui';

const { Title, Text, Paragraph, Link: AntLink } = Typography;

/**
 * Carbon indices CMS.
 *
 * A wizard rather than one long form. Every value on the page is either computed or
 * written, and the two need completely different attention: the numbers want checking
 * against a chart, the words want writing. Presenting all of it at once meant an editor
 * scrolled past nine sections looking for the two that were theirs, and the screen read
 * as a wall of inputs rather than as a page being edited.
 *
 * The steps follow the order the work actually happens in: generate the numbers, check
 * each chart, write the words, then look at the whole thing before it goes anywhere.
 *
 * State lives here and only here. Each step is a view over the same `source` object, so
 * moving back and forth costs nothing and cannot lose an edit -- the steps hold no state
 * of their own beyond which file is selected.
 */

const STEPS = [
  { key: 'generate', title: 'Generate' },
  { key: 'integrity', title: 'Integrity index' },
  { key: 'pricing', title: 'Price index' },
  { key: 'review', title: 'Review' },
] as const;

const EMPTY_SOURCE = {
  as_of: '',
  integrity: {
    quarterly: { periods: [], issuance: [], retirement: [] },
    annual: { periods: [], issuance: [], retirement: [] },
  },
  pricing: { monthly: { periods: [], t1: [], t2: [], t3: [] }, precise_change_pct: null },
  commentary: { integrity: '', pricing: '', note: '' },
};

/**
 * Waits for evidence that a recompute finished, because the response may never arrive.
 *
 * The recompute writes the four series in sequence and takes about a minute and a half.
 * The gateway in front of the API gives up at sixty seconds and returns 504, so the
 * request fails from the browser's point of view while the work carries on and completes
 * on the server. Trusting the HTTP response therefore reports a successful update as a
 * failure -- and every retry starts another full recompute that also appears to fail.
 *
 * So the response is treated as advisory and the data as the truth: this polls until all
 * four indices carry a timestamp from after the run started, which only happens once the
 * last of them has been written.
 */
const RECOMPUTE_POLL_MS = 5000;
const RECOMPUTE_MAX_WAIT_MS = 6 * 60 * 1000;

/**
 * Errors that mean the connection died, not that the work failed.
 *
 * A gateway timeout arrives without a readable response -- the 504 page carries no CORS
 * header, so the browser blocks it and axios reports a bare "Network Error" with no
 * status at all. A real refusal, a 401 or a 500 from the recompute itself, carries one.
 */
const isConnectionGiveUp = (e: any): boolean => {
  const status = e?.status;
  return status === undefined || status === 502 || status === 503 || status === 504;
};

const awaitRecompute = async (
  startedAt: number,
  aborted: () => boolean,
  onProgress: (done: number, total: number) => void,
): Promise<boolean> => {
  const deadline = Date.now() + RECOMPUTE_MAX_WAIT_MS;
  while (Date.now() < deadline) {
    if (aborted()) return false;
    await new Promise((resolve) => setTimeout(resolve, RECOMPUTE_POLL_MS));
    try {
      const view = await getIndicesDraft();
      const stamps = Object.values(view?.indices_computed_at ?? {}) as (string | null)[];
      // How many of the four this run has rewritten. The same comparison the finish
      // condition uses, counted instead of collapsed, so the wait can be shown.
      const done = stamps.filter(
        (t) => !!t && new Date(t as string).getTime() >= startedAt,
      ).length;
      onProgress(done, stamps.length);
      if (stamps.length > 0 && done === stamps.length) return true;
    } catch {
      // A failed poll says nothing about the recompute. Keep waiting.
    }
  }
  return false;
};

const CarbonIndices: React.FC = () => {
  const queryClient = useQueryClient();
  const { token } = theme.useToken();

  const [step, setStep] = useState(0);
  const [source, setSource] = useState<any>(EMPTY_SOURCE);
  const [expectedUpdatedAt, setExpectedUpdatedAt] = useState<string | undefined>();
  const [note, setNote] = useState('');
  const [validation, setValidation] = useState<any>(null);
  /**
   * What the charts and stat cards draw, kept apart from the verdict about it.
   *
   * It used to be read out of `validation`, which `patch()` clears on every edit. So a
   * keystroke in a summary yanked the payload out from under the charts: they fell back to
   * the last saved draft's numbers, or to the "appears here once you load something" box,
   * which is roughly half the height, and everything below it -- including the editor being
   * typed into -- jumped up the page and back again when the recheck landed 700ms later.
   *
   * The prose cannot move a plotted value: derive.ts reads the series and the as-of date,
   * and copies the commentary through untouched. So the verdict is what a keystroke
   * invalidates, and the payload is not.
   */
  const [derived, setDerived] = useState<any>(null);
  const [publishOpen, setPublishOpen] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  const [proposal, setProposal] = useState<any | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  /**
   * The chosen pricing workbook, held here rather than in the step.
   *
   * Step one unmounts when you move on, so a file kept inside it was lost by going
   * forward and coming back -- which matters now that filling the form requires one.
   */
  const [cbmFile, setCbmFile] = useState<File | null>(null);
  // The form opens empty. Nothing is loaded until the editor picks a starting point.
  const [loaded, setLoaded] = useState(false);

  const auth = useAuth();
  // Null on production: see previewUrl.ts. Everything below has to cope with that rather
  // than offer a link to a page that would show a different environment's draft.
  const previewUrl = indicesPreviewUrl();
  const previewLabel = indicesPreviewLabel();
  const liveUrl = indicesLiveUrl();

  const { data: draft, isLoading, isError, error, refetch } = useQuery(
    'carbonIndicesDraft', getIndicesDraft, { retry: false },
  );
  const { data: revisions } = useQuery('carbonIndicesRevisions', getIndicesRevisions, { retry: false });

  useEffect(() => {
    // Only the concurrency token. NOT the note: it describes the change being made, so
    // carrying the previous one forward would file this version in the history under
    // someone else's description.
    if (draft) setExpectedUpdatedAt(draft.updated_at);
  }, [draft]);

  /**
   * Keeps the calculated previews in step with the form.
   *
   * The charts, the heading and the stat cards are derived on the server, so they can only
   * be recomputed by asking it. Debounced, so a burst of typing is one request; the
   * endpoint only computes and never writes.
   */
  useEffect(() => {
    if (!loaded || !source?.as_of) return undefined;
    const timer = setTimeout(() => runValidate(), 700);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source, loaded]);

  /**
   * Every step starts at the top of itself.
   *
   * The steps are long and the bar that moves between them is pinned to the bottom, so
   * going forward from the foot of one screen used to land at the foot of the next: past
   * its heading, its explanation, and usually the first thing it was asking for.
   *
   * The main area is what scrolls -- the sidebar is fixed and scrolls itself -- so this is
   * the window. Instant rather than smooth: a step change is a change of screen, and
   * animating a page-length scroll after it only delays the arrival.
   */
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [step]);

  useEffect(() => {
    if (!dirty) return undefined;
    const handler = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  const patch = (updater: (next: any) => void) => {
    setSource((prev: any) => {
      const next = JSON.parse(JSON.stringify(prev));
      updater(next);
      return next;
    });
    setDirty(true);
    setJustSaved(false);
    // The verdict only. `derived` is left alone: see its declaration.
    setValidation(null);
  };

  const showApiError = (e: unknown, fallback: string) => {
    const err = e as IndicesApiError;
    if (err?.errors?.length) {
      Modal.error({
        title: err.message || fallback,
        width: 760,
        content: <ul className="pl-4">{err.errors.map((x) => <li key={x}>{x}</li>)}</ul>,
      });
    } else {
      message.error(err?.message || fallback);
    }
    if (err?.errorType?.includes('conflict')) refetch();
  };

  /**
   * Recompute, then read the result, as one action.
   *
   * They were two buttons, and in a month's update you press both every time: the numbers
   * are recomputed and then brought into the form. Two clicks for one intention, with the
   * second doing nothing visible until the first had finished.
   *
   * `phase` drives the label rather than two booleans, because the operation is genuinely
   * sequential and the wait is mostly the first half.
   *
   * There was briefly a second control for skipping the recompute, which is cheaper when
   * someone has already run it today. It went: describing that situation took more words
   * than the minute it saved, and a screen with two buttons doing almost the same thing
   * makes the reader stop and choose. One button, one outcome.
   */
  const [phase, setPhase] = useState<'idle' | 'generating' | 'reading'>('idle');

  const readSources = async (files: { cbm?: File }) => {
    setPhase('reading');
    const p = await extractIndices(files);

    if (p?.errors?.length || !p?.source) {
      setProposal(
        p?.source || p?.errors?.length
          ? p
          : { ...p, errors: ['The sources were read but came back with no numbers in them.'] },
      );
      message.error('The sources could not be read. See the reasons above.');
      queryClient.invalidateQueries('carbonIndicesDraft');
      return p;
    }

    // Applied straight away rather than offered for acceptance.
    //
    // There used to be a "Use these numbers" button here, which asked permission for
    // something already asked for: pressing fill IS the request. Nothing is saved by
    // this, nothing is public, and every value stays editable in the steps that follow
    // with a diff against the live page at the end. The review happens after, where the
    // numbers are shown against a chart, not in a modal before anyone can see them.
    // Applied BEFORE the panel that announces it.
    //
    // The panel used to appear as soon as a proposal came back, which meant it could
    // report "numbers are in" while applying them had failed and the form was still
    // empty -- the screen contradicting itself, with Next greyed out beside a green
    // tick. Reporting after the fact makes the claim true by construction.
    startFrom(p.source, {
      validation: p.derived
        ? { valid: true, errors: [], warnings: p.warnings ?? [], derived: p.derived }
        : null,
    });
    setProposal(p);
    message.success('Numbers are in. Check the charts in the next two steps.');
    queryClient.invalidateQueries('carbonIndicesDraft');
    return p;
  };

  /**
   * Whether the indices have been recomputed since this screen was opened.
   *
   * Choosing the workbook starts the whole update, so swapping the file after a mistake
   * would otherwise recompute a second time: a minute of work and another rewrite of the
   * team's sheets, to read a different spreadsheet. The first selection generates; a
   * change of file re-reads what that run produced.
   */
  const [generatedThisSession, setGeneratedThisSession] = useState(false);

  /**
   * How far the recompute has got, for the bar on step one.
   *
   * `since` is when the count last changed, which is what lets the bar keep moving
   * between writes instead of standing still for the half minute or more between them.
   */
  const [recompute, setRecompute] = useState<
    { done: number; total: number; since: number } | null
  >(null);

  // Split from the workbook read: choosing a file used to rewrite the shared sheets too.
  const { mutate: runRecompute } = useMutation(
    async () => {
      setPhase('generating');
      const startedAt = Date.now();
      // Seeded before the first poll, so the bar appears with the click rather than
      // five seconds later.
      setRecompute({ done: 0, total: 4, since: startedAt });

      // Not awaited: the reply only says the run started, and the timestamps decide.
      // A failure here is real though, so it stops the poll.
      let fatal: any = null;
      void startRecompute().catch((e) => {
        if (!isConnectionGiveUp(e)) fatal = e;
      });

      const confirmed = await awaitRecompute(startedAt, () => !!fatal, (done, total) => {
        // Only on a change: twenty-odd polls reporting the same count must not re-render
        // the wizard to say so, and `since` has to mark the write that landed rather than
        // the poll that noticed it had not.
        setRecompute((prev) => (
          prev && prev.done === done
            ? prev
            : { done, total: total || 4, since: Date.now() }
        ));
      });
      if (fatal) throw fatal;
      if (!confirmed) {
        throw new Error(
          'The update did not finish in time. It usually takes about two minutes. '
            + 'Press it again to retry.',
        );
      }
      setGeneratedThisSession(true);
      // So the panel can name the run and the person who asked for it.
      queryClient.invalidateQueries('carbonIndicesDraft');
    },
    {
      onSettled: () => { setPhase('idle'); setRecompute(null); },
      onError: (e) => showApiError(e, 'Could not update the sheets'),
    },
  );

  const { mutate: runReadWorkbook } = useMutation(
    (file: File) => readSources({ cbm: file }),
    {
      onSettled: () => setPhase('idle'),
      onError: (e) => showApiError(e, 'Could not read the workbook'),
    },
  );

  const startFrom = (
    next: any,
    options: { validation?: any; derived?: any; dirty?: boolean } = {},
  ) => {
    setSource(JSON.parse(JSON.stringify(next)));
    setValidation(options.validation ?? null);
    // Whatever this starting point already knows. A blank form passes neither and so
    // starts with no charts, rather than borrowing the previous form's.
    setDerived(options.derived ?? options.validation?.derived ?? null);
    setLoaded(true);
    setDirty(options.dirty ?? true);
    setJustSaved(false);
  };

  const loadFromPublished = () => {
    if (!draft?.published_source) return;
    startFrom(draft.published_source, {
      validation: { valid: true, errors: [], warnings: [], derived: draft.published_derived },
    });
    message.success(`Loaded published v${draft.published_version}. Nothing is saved yet.`);
  };

  /**
   * Picks up the draft already saved on the server.
   *
   * Validation is deliberately left unset. The draft was checked when it was saved, but
   * "valid" on this screen means the server said so about what is on screen now, and the
   * debounced recheck settles that a moment later.
   */
  const loadFromDraft = () => {
    if (!draft?.source) return;
    startFrom(draft.source, { dirty: false, derived: draft.derived });
    setNote('');
    message.success('Loaded the saved draft.');
  };

  const startBlank = () => {
    // No source, no verdict, no charts. An empty form used to borrow the saved draft's
    // charts, because the payload arrived by fallback when nothing had been validated yet
    // and a blank form has no as-of date, so the recheck that would replace it never ran.
    startFrom(EMPTY_SOURCE);
    setNote('');
  };

  const { mutate: runValidate, isLoading: validating } = useMutation(
    () => validateIndicesDraft(source),
    {
      onSuccess: (result) => {
        setValidation(result);
        // Null only when the numbers themselves are unusable, which the server decides:
        // an unwritten paragraph still derives (see validateOnly). So the charts follow
        // the data's validity, not the typing.
        setDerived(result?.derived ?? null);
      },
      // Quiet: this runs while someone is still typing, and a half-typed date is not an
      // event worth a dialog. What is outstanding is listed on the review step.
      onError: () => undefined,
    },
  );

  const { mutate: runSave, isLoading: saving } = useMutation(
    () => saveIndicesDraft({ source, note, expected_updated_at: expectedUpdatedAt }),
    {
      onSuccess: (view) => {
        setExpectedUpdatedAt(view.updated_at);
        setValidation({ valid: true, errors: [], warnings: view.warnings ?? [], derived: view.derived });
        setDerived(view.derived);
        setDirty(false);
        setJustSaved(true);
        queryClient.invalidateQueries('carbonIndicesDraft');
        message.success('Draft saved. Not public yet.');
      },
      onError: (e) => showApiError(e, 'Could not save the draft'),
    },
  );

  const { mutate: runPublish, isLoading: publishing } = useMutation(
    () => publishIndices({ expected_updated_at: expectedUpdatedAt as string, note: note || undefined }),
    {
      onSuccess: () => {
        setPublishOpen(false);
        setDirty(false);
        // No toast. PublishStatus states this at the top of the screen and keeps stating
        // it: a message that disappears after a few seconds is what left editors pressing
        // publish again.
        queryClient.invalidateQueries('carbonIndicesDraft');
        queryClient.invalidateQueries('carbonIndicesRevisions');
      },
      onError: (e) => showApiError(e, 'Could not publish'),
    },
  );

  const { mutate: runRetryDeploy, isLoading: retrying } = useMutation(
    () => retryIndicesDeploy(),
    {
      onSuccess: (r: any) => {
        message[r.deploy?.status === 'dispatched' ? 'success' : 'warning'](r.message);
        queryClient.invalidateQueries('carbonIndicesDraft');
      },
      onError: (e) => showApiError(e, 'Could not ask the website to rebuild'),
    },
  );

  const { mutate: runRestore } = useMutation(
    ({ id }: { id: string }) => restoreIndicesRevision(id, expectedUpdatedAt),
    {
      onSuccess: (view: any) => {
        startFrom(view.source, {
          validation: { valid: true, errors: [], warnings: view.warnings ?? [], derived: view.derived },
        });
        setExpectedUpdatedAt(view.updated_at);
        setHistoryOpen(false);
        setStep(STEPS.length - 1);
        message.success(`${view.message} Nothing is public until you publish.`);
        queryClient.invalidateQueries('carbonIndicesDraft');
      },
      onError: (e) => showApiError(e, 'Could not restore that revision'),
    },
  );

  const roles = auth?.roles ?? [];
  const canEditRole = roles.some((r: string) => EDITOR_ROLES.includes(r));
  const canPublishRole = roles.some((r: string) => PUBLISHER_ROLES.includes(r));

  /** A stored draft carries a concurrency token; a database that never held one does not. */
  const needsToken = !!draft?.updated_at;
  // `matches_published` is the server's own duplicate check: publish copies draft.source
  // into a new row and leaves the draft, so after a publish the two are identical and the
  // server refuses the next one. Reflecting it here means the button goes quiet instead of
  // accepting a click that returns "nothing to publish".
  const alreadyPublished = !!draft?.matches_published;
  const canPublish =
    canPublishRole && !!validation?.valid && !dirty && !!expectedUpdatedAt && !alreadyPublished;

  /** What a person still has to write, in their words. Mirrors the server's own list. */
  const outstanding = useMemo(() => {
    if (!loaded) return [];
    const blank = (html?: string) => !String(html ?? '').replace(/<[^>]*>/g, '').trim();
    const out: string[] = [];
    if (blank(source?.commentary?.integrity)) out.push('The summary beside the integrity chart');
    if (blank(source?.commentary?.pricing)) out.push('The summary beside the price chart');
    if (blank(source?.commentary?.note)) out.push('The note under the price chart');
    return out;
  }, [loaded, source]);

  const revisionColumns = useMemo(() => [
    { title: 'Version', dataIndex: 'version', key: 'version', width: 90,
      render: (v: number) => <Tag color="blue">v{v}</Tag> },
    { title: 'As of', dataIndex: 'as_of', key: 'as_of', width: 110 },
    { title: 'Published', dataIndex: 'published_at', key: 'published_at', width: 180,
      render: (v: string) => (v ? dayjs(v).format('D MMM YYYY HH:mm') : '') },
    { title: 'By', dataIndex: 'published_by_email', key: 'by', width: 230 },
    { title: 'Note', dataIndex: 'note', key: 'note' },
    {
      title: '',
      key: 'restore',
      width: 110,
      render: (_: unknown, row: any) => (
        <Button size="small" disabled={!canEditRole} onClick={() => runRestore({ id: row.id })}>
          Restore
        </Button>
      ),
    },
  ], [canEditRole, runRestore]);

  if (isLoading) {
    return <div className="flex justify-center py-16"><Spin /></div>;
  }

  if (isError) {
    const err = error as IndicesApiError;
    return (
      <Card className="m-4">
        <Alert
          type="error"
          showIcon
          message="Could not load the carbon indices content"
          description={<p className="m-0">{err?.message}</p>}
        />
      </Card>
    );
  }

  const atLast = step === STEPS.length - 1;
  const stepBody = () => {
    switch (STEPS[step].key) {
      case 'generate':
        return (
          <StepGenerate
            indicesComputedAt={draft?.indices_computed_at}
            indicesComputedBy={draft?.indices_computed_by}
            onRecompute={() => runRecompute()}
            phase={phase}
            recompute={recompute}
            indicesSheets={draft?.indices_sheets}
            asOf={source?.as_of ?? ''}
            onAsOf={(value) => patch((next) => { next.as_of = value; })}
            cbmFile={cbmFile}
            alreadyGenerated={generatedThisSession}
            onCbmFile={(file) => {
              setCbmFile(file);
              if (!file) return;

              // Choosing the file IS the instruction, so there is no button after it.
              // Replacing work is the one thing worth a question, and only when there is
              // unsaved work to replace.
              if (dirty) {
                Modal.confirm({
                  title: 'Replace the numbers you have edited?',
                  content:
                    'Reading this workbook puts the indices and its prices over the top of ' +
                    'your unsaved changes. The commentary is never touched.',
                  okText: 'Replace them',
                  onOk: () => runReadWorkbook(file),
                });
                return;
              }
              runReadWorkbook(file);
            }}
            proposal={proposal}
            publishedVersion={draft?.published_version ?? null}
            onLoadPublished={loadFromPublished}
            draftUpdatedAt={draft?.updated_by_email ? draft?.updated_at : null}
            draftUpdatedBy={draft?.updated_by_email}
            onLoadDraft={loadFromDraft}
            onStartBlank={startBlank}
            loaded={loaded}
          />
        );
      case 'integrity':
        return <StepIntegrity source={source} patch={patch} derived={derived} />;
      case 'pricing':
        return <StepPricing source={source} patch={patch} derived={derived} />;
      case 'review':
      default:
        return (
          <StepReview
            derived={derived}
            source={source}
            publishedDerived={draft?.published_derived}
            publishedVersion={draft?.published_version ?? null}
            validation={validation}
            outstanding={outstanding}
            note={note}
            onNote={setNote}
            validating={validating}
          />
        );
    }
  };

  return (
    // The outer element spans the whole main area, right of the sidebar, so the sticky
    // footer can too. Only the reading content is capped, which is what a measure is for.
    <div>
      <div style={{ padding: S.lg, paddingBottom: 0, maxWidth: 1180 }}>
        <Space align="center" wrap className="mb-1">
          <Title level={3} style={{ margin: 0 }}>Carbon indices</Title>
          {draft?.published_version != null && <Tag color="blue">live: v{draft.published_version}</Tag>}
          {dirty ? <Tag color="orange">unsaved changes</Tag>
            : justSaved ? <Tag color="green">saved</Tag> : null}
          <Button size="small" type="link" onClick={() => setHistoryOpen(true)}>History</Button>
        </Space>

        <Paragraph type="secondary">
          Content for{' '}
          <AntLink href={liveUrl} target="_blank" rel="noopener noreferrer">
            the carbon indices page
          </AntLink>
          . One step at a time; nothing is public until the last one.
        </Paragraph>

        <PublishStatus
          deploy={draft?.deploy ?? null}
          version={draft?.published_version ?? null}
          siteLabel={previewLabel}
          siteUrl={liveUrl}
          canRetry={canPublishRole}
          retrying={retrying}
          onRetry={() => runRetryDeploy()}
        />

        {/*
          Clickable, but only backwards and only once something is loaded. Jumping ahead to
          an empty chart teaches nothing, and jumping back to change a number is the whole
          reason the steps are navigable.
        */}
        <Steps
          current={step}
          onChange={(next) => { if (loaded && next < step) setStep(next); }}
          items={STEPS.map((s, i) => ({
            title: s.title,
            disabled: !loaded || i > step,
          }))}
          style={{ marginBottom: S.lg }}
        />

        <Card styles={{ body: { padding: S.lg } }}>
          {!loaded && step > 0 ? (
            <Alert
              type="info"
              showIcon
              message="Nothing loaded yet"
              description="Go back to step 1 and generate the indices, or start from the live page."
            />
          ) : (
            stepBody()
          )}
        </Card>

          {draft?.updated_by_email && (
            <Paragraph type="secondary" style={{ marginTop: S.md, marginBottom: 0 }}>
              Draft last saved by {draft.updated_by_email} at{' '}
              {dayjs(draft.updated_at).format('D MMM YYYY, HH:mm')}.
            </Paragraph>
          )}
      </div>

      {/*
        Sticky, with its own opaque background and a top border.
        
        The steps are long -- the review step is a whole page of charts -- so moving on
        meant scrolling to the end to find the button that does it. A sticky bar keeps the
        way forward where the hand already is.

        Painted deliberately rather than left transparent: a stuck element with no
        background lets the content scroll through it, which is what happened the last time
        something was pinned on this screen.

        Sticky rather than fixed, so it still takes its place in the flow and cannot cover
        the end of the content. It sits outside the capped content column, so it spans the
        full width of the main area rather than stopping at the measure.

        Where you are goes left, where you are going goes right: the actions sit under the
        hand that is already on that side of the screen, and reading position is a label
        rather than a control.
      */}
      <div
        style={{
          position: 'sticky',
          bottom: 0,
          zIndex: 10,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: S.md,
          background: token.colorBgContainer,
          borderTop: `1px solid ${token.colorBorderSecondary}`,
          boxShadow: `0 -2px 8px ${token.colorFillQuaternary}`,
          padding: `${S.md}px ${S.lg}px`,
        }}
      >
        <Text type="secondary">
          Step {step + 1} of {STEPS.length}: {STEPS[step].title}
        </Text>

        <Space wrap align="center" size="middle">
          <Button size="large" disabled={step === 0} onClick={() => setStep((s) => s - 1)}>
            Back
          </Button>

          {!atLast && (
            <Tooltip title={!loaded ? 'Generate the indices first, or start from the live page' : ''}>
              <Button
                size="large"
                type="primary"
                disabled={!loaded}
                onClick={() => setStep((s) => s + 1)}
              >
                Next
              </Button>
            </Tooltip>
          )}

          {atLast && (
            <>
              <Tooltip
                title={
                  !canEditRole ? 'Saving needs the internal_tool:indices_editor role'
                    : needsToken && !expectedUpdatedAt ? 'Still loading the draft'
                    : previewUrl
                      ? `Saves, then opens ${previewLabel} in a new tab showing this draft. Still not public.`
                      : 'Saves the draft. Nothing is public until you publish. '
                        + 'The review step shows how it will read: no site can render a '
                        + 'production draft, so there is nowhere to preview it.'
                }
              >
                <Button
                  size="large"
                  type="primary"
                  disabled={!canEditRole || (needsToken && !expectedUpdatedAt)}
                  onClick={() => {
                    if (!previewUrl) {
                      runSave();
                      return;
                    }
                    /*
                      The tab is opened by the click, not by the save that follows.

                      This button has always said it opens the preview and never did: the
                      save is a round trip, and a browser only allows window.open during a
                      user gesture, so opening it once the draft was stored would be
                      blocked. Opening it now and pointing it at the preview when the save
                      returns keeps it inside the gesture.

                      Not `noopener` in the open call, which makes the call return null in
                      several browsers and leaves nothing to navigate. The reference is
                      severed straight after instead.
                    */
                    const tab = window.open('', '_blank');
                    if (tab) tab.opener = null;
                    runSave(undefined, {
                      onSuccess: () => {
                        if (tab) tab.location.href = previewUrl;
                      },
                      // A draft that did not save has nothing to preview, and the error
                      // dialog is behind this tab.
                      onError: () => tab?.close(),
                    });
                  }}
                  loading={saving}
                >
                  {previewUrl ? 'Save and preview' : 'Save draft'}
                </Button>
              </Tooltip>

              <Tooltip
                title={
                  !canPublishRole ? 'Publishing needs the internal_tool:indices_publisher role'
                    : alreadyPublished ? 'This version is already published. Change something to publish again.'
                    : canPublish ? 'Creates a new public version'
                    : dirty ? 'Save first'
                    : outstanding.length ? 'The commentary is not written yet'
                    : 'Checking the numbers'
                }
              >
                <Button size="large" danger disabled={!canPublish} onClick={() => setPublishOpen(true)}>
                  Publish
                </Button>
              </Tooltip>

              {!canPublish && (
                <Text type="secondary">
                  {!canPublishRole ? 'Someone with the publisher role takes it from here.'
                    : alreadyPublished ? 'Nothing new to publish. This version is already out.'
                    : dirty ? 'Save before publishing.'
                    : outstanding.length ? 'Write the summaries on the two index steps.'
                    : 'Checking the numbers.'}
                </Text>
              )}
            </>
          )}
        </Space>
      </div>

      <Modal
        open={historyOpen}
        onCancel={() => setHistoryOpen(false)}
        footer={null}
        width={980}
        title="Published versions"
      >
        <Paragraph type="secondary">
          Restoring copies a version back into the draft for review. It does not publish:
          a rollback goes through the same steps as any other change.
        </Paragraph>
        <Table
          size="small"
          rowKey="id"
          dataSource={revisions ?? []}
          columns={revisionColumns}
          pagination={{ pageSize: 8, size: 'small' }}
        />
      </Modal>

      <Modal
        open={publishOpen}
        onCancel={() => setPublishOpen(false)}
        onOk={() => runPublish()}
        okText="Publish"
        okButtonProps={{ danger: true, loading: publishing }}
        title="Publish to calyxglobal.com?"
      >
        <Paragraph>
          This creates version {(draft?.published_version ?? 0) + 1} and asks the website to
          rebuild. The public page will show these numbers.
        </Paragraph>
        {!!validation?.warnings?.length && (
          <Alert
            type="warning"
            showIcon
            message="Warnings on this revision"
            description={
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {validation.warnings.map((w: string) => <li key={w}>{w}</li>)}
              </ul>
            }
          />
        )}
      </Modal>
    </div>
  );
};

export default CarbonIndices;
